import type { AccountInfo } from '@azure/msal-browser';

/**
 * Azure AD group required to access Stellar.
 *
 * Configured per-environment via REACT_APP_AZURE_REQUIRED_GROUP (set through the CircleCI
 * contexts, mirroring the other REACT_APP_AZURE_* values). The group name is intentionally
 * NOT hardcoded in the codebase. When it is not configured, access is denied.
 */
export function getRequiredGroup(): string {
  return (process.env.REACT_APP_AZURE_REQUIRED_GROUP ?? '').trim();
}

type TokenClaims = AccountInfo['idTokenClaims'];

/**
 * Extract the caller's group membership from ID token claims.
 *
 * Azure AD B2C may emit groups either as a JSON array ("groups") or as a comma-delimited
 * string ("groupsCommaDelimited"), depending on policy configuration. Handle both,
 * preferring the array form.
 */
export function extractGroups(claims: TokenClaims | undefined): string[] {
  if (!claims) {
    return [];
  }

  const groups = claims.groups;
  if (Array.isArray(groups)) {
    return groups.map(String);
  }

  const delimited = (claims as Record<string, unknown>).groupsCommaDelimited;
  if (typeof delimited === 'string' && delimited) {
    return delimited
      .split(',')
      .map((group) => group.trim())
      .filter(Boolean);
  }

  return [];
}

/**
 * Whether the caller is authorized to use Stellar based on their group membership.
 * Denies (returns false) when no required group is configured.
 */
export function isUserAuthorized(claims: TokenClaims | undefined): boolean {
  const requiredGroup = getRequiredGroup();
  if (!requiredGroup) {
    return false;
  }
  return extractGroups(claims).includes(requiredGroup);
}
